/* eslint-disable no-restricted-imports */
import React, {useEffect, useState} from 'react'
import styles from './UIGameEnd.module.scss'
import {UIGameEndProvider, UIGameEndProviderProps, useUIGameEndContext} from '../../contexts/UIGameEndContext'
import {UIGameEndHeader} from './UIGameEndHeader'
import {UIGameEndContent} from './UIGameEndContent'
import {UIGameEndFooter} from './UIGameEndFooter'
import {UIGameEndRankedScreen} from './UIGameEndRankedScreen'
import Confetti from 'react-confetti'
import {Sound} from '../../utils/sound'
import {UIUtilsEjsVariables} from '../../../../../code/client/shared/scripts/UIUtilsEjsVariables'
import {ClientUserState} from '../../../../../code/client/shared/scripts/ClientUserState'
import {ViewPopupEndgameSignup} from '../../../../../code/client/shared/scripts/ui_popup/ViewPopupEndgameSignup'
import {useUIGameReplayContext} from '../../contexts/UIGameReplayContext'

type GlobalGameEndProviderProps = Omit<UIGameEndProviderProps, 'children'>
let globalGameEndProviderProps: undefined | ((gameEndProviderProps: GlobalGameEndProviderProps) => void)
export function setGlobalGameEndProviderProps(gameEndProviderProps: GlobalGameEndProviderProps) {
    if(globalGameEndProviderProps) globalGameEndProviderProps(gameEndProviderProps)
}

let globalGameEndNumberOfPlayersJoinedRematch: undefined | ((numberOfPlayersJoinedRematch: number) => void)
export function setGlobalGameEndNumberOfPlayersJoinedRematch(numberOfPlayersJoinedRematch: number) {
    if(globalGameEndNumberOfPlayersJoinedRematch) globalGameEndNumberOfPlayersJoinedRematch(numberOfPlayersJoinedRematch)
}

let globalSetGameEndIsVisible: undefined | ((isVisible: boolean) => void)
let globalGetGameEndIsVisible: undefined | boolean
export function setGlobalGameEndIsVisible(isVisible: boolean) {
    if(globalSetGameEndIsVisible) globalSetGameEndIsVisible(isVisible)
}

export function getGlobalGameEndIsVisible(): boolean {
    return globalGetGameEndIsVisible ?? false
}

export const UIGameEnd = ({
    confettiWidth,
    confettiHeight,
}: {
    confettiWidth: number
    confettiHeight: number
}) => {
    const [gameEndProviderProps, setGameEndProviderProps] = useState<GlobalGameEndProviderProps | null>(null)
    const [numberOfPlayersJoinedRematch, setNumberOfPlayersJoinedRematch] = useState(0)
    const [isVisible, setIsVisible] = useState(true)
    const [disableAnimation, setDisableAnimation] = useState(false)

    globalGameEndProviderProps = setGameEndProviderProps
    globalGameEndNumberOfPlayersJoinedRematch = setNumberOfPlayersJoinedRematch
    globalSetGameEndIsVisible = setIsVisible
    globalGetGameEndIsVisible = isVisible

    if(!gameEndProviderProps) return null
    if(!isVisible) return null

    return <UIGameEndProvider {...gameEndProviderProps} numberOfPlayersJoinedRematch={numberOfPlayersJoinedRematch} >
        <UIGameEndComponent
            confettiHeight={confettiHeight}
            confettiWidth={confettiWidth}
            disableAnimation={disableAnimation}/>
    </UIGameEndProvider>
}

export interface UIGameEndComponentProps {
    confettiHeight: number
    confettiWidth: number
    disableAnimation: boolean
}

export function UIGameEndComponent({
    confettiHeight,
    confettiWidth,
    disableAnimation,
}: UIGameEndComponentProps) {
    const {isReplay} = useUIGameReplayContext()
    const {isPlayerHasRankedState, isMyColorWinner} = useUIGameEndContext()
    const [showRankedScreen, setShowRankedScreen] = useState(isPlayerHasRankedState && !isReplay)
    const [runConfetti, setRunConfetti] = useState(false)

    const playVictorySound = () => {
        // Original issue: https://github.com/colonistio/katan/pull/9949
        // TODO Eventually replace the victory sound with the end game music and play for all players
        // Sound.playSound(Sound.SoundType.MusicEgoSum)

        if(!isMyColorWinner) return
        if(isReplay) return

        Sound.playSound(Sound.SoundType.Victory)
    }

    const showSignupPopup = () => {
        if(UIUtilsEjsVariables.isUserOnDiscord) return
        if(UIUtilsEjsVariables.isUserOnMobileApp) return
        if(ClientUserState.userState?.isLoggedIn) return

        const gameCountMilestones = [5, 10, 25]
        const postMilestonesInterval = 50
        const completedGameCount = (ClientUserState.userState?.totalCompletedGameCount ?? 0) + 1
        const isHardCodedGameCount = gameCountMilestones.includes(completedGameCount)
        const passedTheHardcodedGameCounts = completedGameCount > Math.max(...gameCountMilestones)
        const hitMilestoneInterval = completedGameCount % postMilestonesInterval == 0

        const shouldShowSignupPopup = isHardCodedGameCount || (passedTheHardcodedGameCounts && hitMilestoneInterval)
        if(!shouldShowSignupPopup) return

        const popup = new ViewPopupEndgameSignup(completedGameCount)
        popup.show()
    }

    useEffect(() => {
        setRunConfetti(true)
        playVictorySound()
        showSignupPopup()
    }, [])

    return (
        <div className={styles.container}>
            {isMyColorWinner && !isReplay && runConfetti &&
                <Confetti
                    className={styles.confetti}
                    initialVelocityY={-10}
                    numberOfPieces={175}
                    recycle={false}
                    width={confettiWidth}
                    height={confettiHeight}
                />
            }
            <UIGameEndHeader disableAnimation={isReplay || disableAnimation}/>
            {showRankedScreen ? <UIGameEndRankedScreen
                onContinue={() => {
                    setShowRankedScreen(false)
                }}/> : <>
                <UIGameEndContent disableAnimation={isReplay || disableAnimation}/>
                <UIGameEndFooter disableAnimation={isReplay || disableAnimation}/>
            </>}
        </div>
    )
}
